import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const FeaturedChallenge = ({ challenge }) => {
  const navigate = useNavigate();

  const handleJoinChallenge = () => {
    navigate('/giving-challenges');
  };

  const formatTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff <= 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const getProgressPercentage = () => {
    return Math.min((challenge?.currentParticipants / challenge?.targetParticipants) * 100, 100);
  };

  return (
    <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-6 shadow-elevation-2">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Trophy" size={24} color="white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-card-foreground">{challenge?.title}</h3>
            <p className="text-sm text-muted-foreground">Featured Challenge</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-warning">{formatTimeRemaining(challenge?.endDate)}</p>
          <p className="text-xs text-muted-foreground">Time remaining</p>
        </div>
      </div>
      <p className="text-card-foreground mb-4">{challenge?.description}</p>
      {/* Challenge Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-primary">{challenge?.currentParticipants}</p>
          <p className="text-xs text-muted-foreground">Participants</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-success">${challenge?.totalRaised?.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Raised</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-secondary">{challenge?.causesSupported}</p>
          <p className="text-xs text-muted-foreground">Causes</p>
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">Participation Goal</span>
          <span className="font-medium text-card-foreground">
            {challenge?.currentParticipants} / {challenge?.targetParticipants}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-primary to-secondary h-3 rounded-full transition-all duration-500"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
      </div>
      {/* Recent Participants */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="flex -space-x-2">
            {challenge?.recentParticipants?.slice(0, 4)?.map((participant, index) => (
              <div 
                key={index}
                className="w-8 h-8 bg-primary rounded-full border-2 border-card flex items-center justify-center text-xs text-primary-foreground font-medium"
              >
                {participant?.name?.charAt(0)}
              </div>
            ))}
            {challenge?.recentParticipants?.length > 4 && (
              <div className="w-8 h-8 bg-muted rounded-full border-2 border-card flex items-center justify-center text-xs text-muted-foreground">
                +{challenge?.recentParticipants?.length - 4}
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground">Recent participants</p>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex space-x-3">
        <Button
          onClick={handleJoinChallenge}
          className="flex-1"
          iconName="Users"
          iconPosition="left"
        >
          Join Challenge
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/giving-challenges')}
          iconName="Info"
          iconPosition="left"
        >
          Details
        </Button>
      </div>
    </div>
  );
};

export default FeaturedChallenge;